import { Link } from "react-router-dom";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/24/outline";
import { checklistTasks } from "./checklistTasks";
import { useOnboardingStore } from "../useOnboardingStore";

interface ChecklistPanelProps {
  onNavigate: () => void;
}

/**
 * What's left, inside the getting-started popover.
 *
 * Also the way back into a guided run that was closed — without that, closing
 * the rail is a one-way door, and someone who wanted to look around first has
 * no route back to it.
 */
export default function ChecklistPanel({ onNavigate }: ChecklistPanelProps) {
  const tasks = useOnboardingStore((s) => s.state?.checklist?.tasks ?? {});
  const rail = useOnboardingStore((s) => s.state?.rail);
  const markTaskDone = useOnboardingStore((s) => s.markTaskDone);
  const resumeRail = useOnboardingStore((s) => s.resumeRail);
  const dismissChecklist = useOnboardingStore((s) => s.dismissChecklist);

  const doneCount = checklistTasks.filter((t) => tasks[t.id]).length;

  // Only offered for a run that was closed part-way — a finished one has
  // nothing to resume, and the rail is not reachable before it is minted.
  const canResume = !!rail?.exitedAt && !rail.completedAt;
  const railTotal = rail?.stepIds.length ?? 0;
  const railDone = Math.min(rail?.step ?? 0, railTotal);

  return (
    <div className="w-80 rounded-lg border border-gray-700 bg-gray-800 shadow-xl">
      <div className="border-b border-gray-700 px-4 py-3">
        <div className="text-sm font-semibold text-gray-100">
          Getting started
        </div>
        <div className="mt-0.5 text-xs text-gray-400">
          {doneCount} of {checklistTasks.length} done — each one makes the
          platform work harder for you.
        </div>
      </div>

      <ul className="max-h-80 overflow-y-auto p-2">
        {canResume && (
          <li className="flex items-start gap-2.5 rounded-md px-2 py-2 hover:bg-gray-700/40">
            <ArrowUturnLeftIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-squawk-blue"
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-200">
                Finish the walkthrough
              </div>
              <p className="mt-0.5 text-xs text-gray-400">
                {railDone} of {railTotal} steps done.
              </p>
              <button
                type="button"
                onClick={() => {
                  resumeRail();
                  onNavigate();
                }}
                className="mt-1 inline-block cursor-pointer border-none bg-transparent p-0 text-xs font-medium text-squawk-blue hover:underline"
              >
                Pick it back up
              </button>
            </div>
          </li>
        )}

        {checklistTasks.map((task) => {
          const done = !!tasks[task.id];
          return (
            <li
              key={task.id}
              className="flex items-start gap-2.5 rounded-md px-2 py-2 hover:bg-gray-700/40"
            >
              <CheckCircleIcon
                className={`mt-0.5 h-5 w-5 shrink-0 ${
                  done ? "text-green-500" : "text-gray-600"
                }`}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div
                  className={`text-sm font-medium ${
                    done ? "text-gray-500 line-through" : "text-gray-200"
                  }`}
                >
                  {task.title}
                </div>
                {!done && (
                  <>
                    <p className="mt-0.5 text-xs text-gray-400">{task.blurb}</p>
                    {task.externalHref ? (
                      <a
                        href={task.externalHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          // Store CTAs self-complete — we can't observe the
                          // install itself.
                          markTaskDone(task.id);
                        }}
                        className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-squawk-blue hover:underline"
                      >
                        {task.ctaLabel}
                        <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                      </a>
                    ) : task.href ? (
                      <Link
                        to={task.href}
                        onClick={onNavigate}
                        className="mt-1 inline-block text-xs font-medium text-squawk-blue hover:underline"
                      >
                        {task.ctaLabel}
                      </Link>
                    ) : null}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-gray-700 px-4 py-2 text-right">
        <button
          type="button"
          onClick={dismissChecklist}
          className="text-xs text-gray-500 hover:text-gray-300 transition-colors bg-transparent border-none cursor-pointer"
        >
          Dismiss checklist
        </button>
      </div>
    </div>
  );
}
